import React, { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

// Viewport-lazy <video> (2026-07-18, homepage media-weight pass).
//
// PROBLEM: `<video autoPlay preload="metadata">` forces the browser to
// download the ENTIRE mp4 on page load — autoplay overrides the preload
// hint. The two homepage videos (hero-drone 2.4MB + install-beavercreek
// 2.5MB) were ~5MB of eager transfer before the user scrolled anywhere.
//
// FIX: render the <video> element with ONLY its poster until the element
// is within ~200px of the viewport (IntersectionObserver). The poster
// paints instantly in the exact same box (same classes/styles → zero
// layout shift); the mp4 `src` is attached — and playback started — only
// when the user actually approaches the section.
//
// prefers-reduced-motion: never autoplay. The poster stays up with a
// play button overlay; the mp4 downloads/plays only on explicit tap.
//
// Rendered as `motion.video` so callers can keep passing framer-motion
// `variants` (e.g. the RecentInstalls hover-zoom) — with no variants the
// motion wrapper is inert.
const LazyVideo = ({
  src,
  poster,
  className = '',
  style,
  variants,
  rootMargin = '200px 0px',
  'aria-label': ariaLabel,
}) => {
  const videoRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();
  const [nearViewport, setNearViewport] = useState(false);
  const [userStarted, setUserStarted] = useState(false);

  // Load + autoplay when: near viewport AND (motion OK, or user tapped play).
  const shouldLoad = (nearViewport && !prefersReducedMotion) || userStarted;

  useEffect(() => {
    const el = videoRef.current;
    if (!el || nearViewport) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      // Ancient browser fallback — behave like the old eager video.
      setNearViewport(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries?.some((entry) => entry.isIntersecting)) {
          setNearViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [nearViewport, rootMargin]);

  // Start playback once the src is attached. The explicit play() call
  // (instead of the autoPlay attribute) is deliberate: the element mounts
  // WITHOUT a src, and browsers only honor autoPlay reliably at initial
  // load — after a dynamic src attach, play() is the dependable path.
  useEffect(() => {
    if (!shouldLoad) return;
    const el = videoRef.current;
    if (!el) return;
    const p = el.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => {
        /* Autoplay rejected (browser policy) — poster remains, no crash. */
      });
    }
  }, [shouldLoad]);

  return (
    <>
      <motion.video
        ref={videoRef}
        variants={variants}
        src={shouldLoad ? src : undefined}
        poster={poster}
        muted
        playsInline
        preload={shouldLoad ? 'auto' : 'none'}
        className={className}
        style={style}
        aria-label={ariaLabel}
      />
      {prefersReducedMotion && !userStarted && (
        <button
          type="button"
          onClick={() => setUserStarted(true)}
          aria-label={ariaLabel ? `Play: ${ariaLabel}` : 'Play video'}
          className="absolute inset-0 z-10 flex items-center justify-center"
          style={{ background: 'rgba(0, 0, 0, 0.15)' }}
        >
          <span
            className="flex items-center justify-center w-16 h-16 rounded-full"
            style={{
              background: 'rgba(255, 255, 255, 0.9)',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="#000150"
              aria-hidden="true"
              style={{ marginLeft: 3 }}
            >
              <path d="M8 5v14l11-7z" />
            </svg>
          </span>
        </button>
      )}
    </>
  );
};

export default LazyVideo;
